import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  SarrafiyeCalc,
  TrLinkMatrix,
  FaqAccordion,
  buildTrSchema,
  trDateParts,
  goldRatesQueryOptions,
  TRY_PER_USD,
  GRAMS_PER_OZ,
  formatTry,
} from "@/components/tr/shell";
import { useGoldRates } from "@/lib/nonblocking-rates";

const SLUG = "/tr/ons-altin-grafik-yorumlari";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "Ons altın grafik hareketleri serbest piyasa altın kurunu nasıl etkiler?", a: "Ons altın (XAU/USD) global referans fiyattır; USD bazlı işlem görür. Türkiye'deki gram altın fiyatı = (Ons × USD/TRY) / 31.1035 formülüyle hesaplanır. Ons yükselirse ya da USD/TRY yükselirse — her iki durumda da TL bazında gram altın primlenir." },
  { q: "Son dakika İslam Memiş altın yorumları ve analizleri ne yönde?", a: "Uzman ekonomist yorumları FED faiz kararları, ABD enflasyonu (CPI/PPI), jeopolitik gerilimler ve merkez bankası alımları üzerine yoğunlaşır. Yatırım kararı almadan önce her zaman birden fazla bağımsız kaynak ve resmi ekonomi takvimlerini kontrol edin." },
  { q: "Bugün gram altın ne kadar ve anlık altın fiyatları nasıl takip edilir?", a: "Sayfamızın üstündeki canlı grafik ve tabloda gram altın satış değerini anlık TL olarak görebilirsiniz. Grafik son 30 günlük trendi gösterir; tablo ise sarrafiye kırılımını Alış-Satış-Fark% şeklinde listeler." },
  { q: "Ons altın kaç dolar bugün ve TL karşılığı nedir?", a: "Ons altın değeri XAU/USD spot piyasasından anlık güncellenir. TL karşılığı = Ons USD × USD/TRY kuru. Örneğin ons $4000 ve USD/TRY 34.5 iken ons altının TL karşılığı yaklaşık 138.000 TL olur." },
];

export const Route = createFileRoute("/tr/ons-altin-grafik-yorumlari")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = trDateParts();
    const title = `Canlı Ons Altın Grafiği ve Uzman Altın Yorumları — XAU/USD TL Bazlı`;
    const description = `Ons altın grafik, XAU/USD anlık kur, TL karşılığı ve son dakika altın yorumları burada. FED faiz, CPI ve jeopolitik risk analizleri dahil canlı takip ekranı.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "tr-TR" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "tr_TR" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "tr-TR", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildTrSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: SpotMarketPage,
});

function SpotMarketPage() {
  const dp = trDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">Türkiye · Spot Piyasa</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          Canlı Ons Altın Grafiği ve Uzman Altın Yorumları ({dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          Ons altın (XAU/USD) global referans değerdir; USD bazında işlem görür. Türk Lirası karşılığı USD/TRY kuruna ve grama dönüşüm formülüne bağlıdır. Aşağıdaki grafik ve XAU/USD ↔ TL dönüşüm tablosu piyasayı iki taraflı takip etmenizi sağlar.
        </p>
      </header>
      <LiveChart title={`Ons Altın (XAU/USD) TL Bazlı Grafik — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <OnsUsdTable />
      <AnalystDigest />
      <SarrafiyeCalc />
      <FaqAccordion items={FAQS} />
      <TrLinkMatrix current={SLUG} />
    </main>
  );
}

function OnsUsdTable() {
  const { data } = useGoldRates();
  const spot = data.spotUsdPerOz;
  const rows = [
    { name: "Ons Altın (XAU)", usd: spot, weight: "1 ons (31.1035 g)" },
    { name: "Yarım Ons", usd: spot / 2, weight: "15.55 g" },
    { name: "Çeyrek Ons", usd: spot / 4, weight: "7.78 g" },
    { name: "Gram (24 Ayar)", usd: spot / GRAMS_PER_OZ, weight: "1 g saf" },
  ];
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <h2 className="text-xl md:text-2xl font-bold mb-3">Ons Altın USD ↔ TL Dönüşüm Tablosu</h2>
      <p className="text-sm text-muted-foreground mb-3">USD/TRY referans kur: <span className="font-semibold tabular-nums">{TRY_PER_USD.toFixed(2)}</span></p>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2">Ürün</th>
              <th className="px-3 py-2 text-right">Ağırlık</th>
              <th className="px-3 py-2 text-right">USD</th>
              <th className="px-3 py-2 text-right">TL Karşılığı</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-amber-500/10">
                <th className="px-3 py-2 text-left font-medium">{r.name}</th>
                <td className="px-3 py-2 text-right text-muted-foreground">{r.weight}</td>
                <td className="px-3 py-2 text-right">${r.usd.toLocaleString("en-US", { maximumFractionDigits: 2 })}</td>
                <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-300">{formatTry(r.usd * TRY_PER_USD)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AnalystDigest() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <div className="rounded-2xl border border-border bg-muted/30 p-5">
        <h2 className="text-lg md:text-xl font-bold mb-3">Ons Altın Uzman Analiz Notları — Ana Başlıklar</h2>
        <ul className="grid gap-2 sm:grid-cols-2 text-sm">
          <li className="rounded-lg border border-border bg-card p-3"><strong>FED Faiz Kararı:</strong> Faiz indirimi beklentisi ons altını destekler; agresif duruş baskılar.</li>
          <li className="rounded-lg border border-border bg-card p-3"><strong>Merkez Bankası Alımları:</strong> Çin, Türkiye ve Rusya merkez bankası altın rezervi artışı fiyata yapısal talep katar.</li>
          <li className="rounded-lg border border-border bg-card p-3"><strong>Jeopolitik Risk:</strong> Orta Doğu ve Ukrayna gerginliği safe haven talebini tetikler.</li>
          <li className="rounded-lg border border-border bg-card p-3"><strong>ABD Enflasyonu (CPI):</strong> Yüksek CPI reel faizleri baskılar, altına dolaylı destek verir.</li>
          <li className="rounded-lg border border-border bg-card p-3"><strong>DXY (Dolar Endeksi):</strong> DXY düşüşü ons altın için ters ilişki nedeniyle olumludur.</li>
          <li className="rounded-lg border border-border bg-card p-3"><strong>USD/TRY Etkisi:</strong> TL zayıflaması, ons düşse bile gram altını TL bazında yükseltebilir.</li>
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">Not: Yukarıdaki başlıklar bilgi amaçlıdır; yatırım tavsiyesi değildir. Kararlarınız için lisanslı danışmanınıza başvurunuz.</p>
      </div>
    </section>
  );
}